import React, { useState, useEffect, useMemo } from 'react';

import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';
import { Typography, Spinner } from "stories/components";
import { roundScaleDecimals } from "services/helpers"
import { httpGetSecurityPerformance} from "services/portfolios"

function normalizePortfolioIds(portfolio, portfolioList) {
  const raw = portfolio ?? portfolioList;
  if (raw == null) return [];
  return Array.isArray(raw) ? raw.filter(Boolean) : [raw];
}

function normalizePerformanceList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.securities)) return data.securities;
  return [];
}

function getPercentField(period) {
  if (period === "ytd") return "first_day_of_year_change_percent";
  if (period === "1y") return "one_year_ago_change_percent";
  if (period === "all") return "market_value_change_percent";
  return "one_month_ago_change_percent";
}

function getItemName(item) {
  return item?.name || item?.related_model?.name || item?.related_model?.[0]?.name;
}

function getItemPercent(item, percentField) {
  const direct = item?.[percentField];
  if (direct != null && direct !== "" && !Number.isNaN(Number(direct))) {
    return roundScaleDecimals(direct, 2);
  }

  if (item?.market_value != null && item?.value_total_summary) {
    const change = Number(item.market_value) + Number(item.value_total_summary);
    const base = Number(item.value_total_summary) * -1;
    if (base !== 0 && Number.isFinite(change) && Number.isFinite(base)) {
      const pct = (change / base) * 100;
      if (Number.isFinite(pct)) return roundScaleDecimals(pct, 2);
    }
  }

  return null;
}

function mapRowsToPerformanceSource(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows.map((row) => ({
    ...row,
    name: getItemName(row),
    related_model: row.related_model,
    market_value: row.market_value,
    value_total_summary: row.value_total_summary,
    market_value_change_percent: row.market_value_change_percent,
    one_month_ago_change_percent: row.one_month_ago_change_percent,
    one_year_ago_change_percent: row.one_year_ago_change_percent,
    first_day_of_year_change_percent: row.first_day_of_year_change_percent,
  }));
}

const BestWorstPerformance = ({
  portfolio,
  portfolio_list,
  rows,
  date,
  sectors,
  related_model_id,
  currency,
  related_model_type,
  bestPerfomanceTimePeriod = "month",
}) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [usedRowsFallback, setUsedRowsFallback] = useState(false);

  const portfolioIds = useMemo(
    () => normalizePortfolioIds(portfolio, portfolio_list),
    [portfolio, portfolio_list]
  );

  const portfolioIdsKey = portfolioIds.join(',');

  const investmentTypeIds = useMemo(
    () => related_model_type?.filter((item) => item.id !== "all").map((item) => item.id),
    [related_model_type]
  );

  const investmentTypeKey = (investmentTypeIds || []).join(',');

  const rowsKey = useMemo(
    () => (Array.isArray(rows) ? rows.map((r) => `${r.related_model_id}:${r.related_model_type}`).join('|') : ''),
    [rows]
  );

  useEffect(() => {
    if (!portfolioIds.length || !date) {
      setChartData([]);
      setUsedRowsFallback(false);
      return;
    }

    const params = {
      portfolio: portfolioIds,
      put_together: portfolioIds.length > 1 ? true : undefined,
      for_investime: true,
      date_transaction: date,
      date: date,
      date__lte: date,
      selected_currency: currency,
      currency: currency,
      sectors: sectors?.length ? sectors : undefined,
      investmentType: investmentTypeIds?.length ? investmentTypeIds : undefined,
      related_model_id: related_model_id,
    };

    if (Array.isArray(rows) && rows.length > 0) {
      params.securities = rows.map((row) => ({
        related_model_id: row.related_model_id,
        related_model_type: row.related_model_type,
      }));
    }

    let cancelled = false;
    setLoading(true);
    setFetchError(false);
    setUsedRowsFallback(false);

    httpGetSecurityPerformance(params)
      .then((response) => {
        if (cancelled) return;
        const list = normalizePerformanceList(response?.data);
        if (list.length > 0) {
          setChartData(list);
          return;
        }
        const fallback = mapRowsToPerformanceSource(rows);
        setChartData(fallback);
        setUsedRowsFallback(fallback.length > 0);
      })
      .catch(() => {
        if (cancelled) return;
        const fallback = mapRowsToPerformanceSource(rows);
        setChartData(fallback);
        setUsedRowsFallback(fallback.length > 0);
        setFetchError(fallback.length === 0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [portfolioIdsKey, date, currency, sectors, related_model_id, investmentTypeKey, rowsKey, rows, investmentTypeIds]);

  const { bestPerformers, worstPerformers } = useMemo(() => {
    if (!chartData.length) {
      return { bestPerformers: [], worstPerformers: [] };
    }

    const percentField = getPercentField(bestPerfomanceTimePeriod);
    const withPercent = chartData
      .map((item) => {
        const percent = getItemPercent(item, percentField);
        return {
          ...item,
          name: getItemName(item),
          market_value_change_percent_show: percent,
        };
      })
      .filter((item) => item.name && item.market_value_change_percent_show != null);

    if (!withPercent.length) {
      return { bestPerformers: [], worstPerformers: [] };
    }

    const sorted = [...withPercent].sort(
      (a, b) => Number(b.market_value_change_percent_show) - Number(a.market_value_change_percent_show)
    );

    return {
      bestPerformers: sorted.slice(0, 3),
      worstPerformers: sorted.slice(-3).reverse(),
    };
  }, [chartData, bestPerfomanceTimePeriod]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner />
      </div>
    );
  }

  if (fetchError) {
    return (
      <Typography variant="small" className="text-muted px-2 py-3">
        {t("performance_load_error", "Nousijoiden ja laskijoiden tietoja ei voitu hakea.")}
      </Typography>
    );
  }

  if (!bestPerformers.length && !worstPerformers.length) {
    return (
      <Typography variant="small" className="text-muted px-2 py-3">
        {Array.isArray(rows) && rows.length > 0
          ? t("performance_no_data", "Ei muutostietoja valitulla jaksolla.")
          : t("performance_load_hint", "Hae salkun yhteenveto nähdäksesi nousijat ja laskijat.")}
      </Typography>
    );
  }

  const renderTable = (items, keyPrefix) => (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <tbody>
        {items.map((item, index) => (
          <tr key={`${keyPrefix}-${index}`}>
            <td className="pl-2" style={{ borderBottom: "1px solid #ddd" }}>
              <Typography variant="small">{item.name}</Typography>
            </td>
            <td
              className={`${item.market_value_change_percent_show > 0 ? "text-success" : "text-danger"} pl-2`}
              style={{ borderBottom: "1px solid #ddd", textAlign: "right" }}
            >
              <Typography
                variant="small"
                className={item.market_value_change_percent_show > 0 ? "text-success" : "text-danger"}
              >
                {item.market_value_change_percent_show}%
              </Typography>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div style={{ width: "100%" }}>
      {usedRowsFallback && (
        <Typography variant="small" className="text-muted px-2 pb-2">
          {t("performance_rows_fallback_hint", "Jaksokohtaista dataa ei saatavilla — näytetään kokonaismuutos.")}
        </Typography>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr className="bg-light">
            <th className="px-2 py-1" style={{ borderBottom: "1px solid #ddd", textAlign: "left" }}>
              <Typography variant="small">{t("performance_winners", "Nousijat")}</Typography>
            </th>
            <th className="px-2 py-1" style={{ borderBottom: "1px solid #ddd", textAlign: "right" }}>
              <Typography variant="small">{t("performance_performance", "Muutos")}</Typography>
            </th>
          </tr>
        </thead>
      </table>
      {renderTable(bestPerformers, "best")}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr className="bg-light">
            <th className="px-2 py-1" style={{ borderBottom: "1px solid #ddd", textAlign: "left" }}>
              <Typography variant="small">{t("performance_losers", "Laskijat")}</Typography>
            </th>
            <th className="px-2 py-1" style={{ borderBottom: "1px solid #ddd", textAlign: "right" }}>
              <Typography variant="small">{t("performance_performance", "Muutos")}</Typography>
            </th>
          </tr>
        </thead>
      </table>
      {renderTable(worstPerformers, "worst")}
    </div>
  );
};

BestWorstPerformance.propTypes = {
  portfolio: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  portfolio_list: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  rows: PropTypes.array,
  date: PropTypes.string,
  sectors: PropTypes.array,
  related_model_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currency: PropTypes.string,
  related_model_type: PropTypes.array,
  bestPerfomanceTimePeriod: PropTypes.string,
};

export default BestWorstPerformance;
